import { Component, OnInit } from '@angular/core';
import { LoadingAndErrors } from '../../../../../classes';
import { MERCHANT_TYPE, PAYMENT_TYPE, VOLUME_DISCOUNT_UNITS, PARTNER_OBJECT_TYPE, SYSTEM } from '../../../../../enums';
import { BsModalRef } from 'ngx-bootstrap';
import { VolumeDiscountRateRequestApi } from '../../../../../../assets/js/lb-sdk/services/custom/VolumeDiscountRateRequest';
import { UserDataApi, PartnerApi, StandardDiscountApi, DiscountRequestApi, ConfigurationApi } from '../../../../../../assets/js/lb-sdk';
import { ProductsService, DiscountsService } from '../../../../../services';
import { ToastrService } from 'ngx-toastr';
import { MerchantTypePipe } from '../../../../../pipes/merchant-type.pipe';
import { PaymentTypePipe } from '../../../../../pipes/payment-type.pipe';
import { SapMerchantTypePipe } from '../../../../../pipes/sap-merchant-type.pipe';
import { SapPaymentTypePipe } from '../../../../../pipes/sap-payment-type.pipe';
import { finalize } from 'rxjs/operators';
import { VolumeDiscountRateApi } from '../../../../../../assets/js/lb-sdk/services/custom/VolumeDiscountRate';

interface VolumeDiscountForm {
  id?: string,
  partnerId: string,
  productType: { id: string, name: string },
  paymentType: number,
  merchantType: number,
  discount: number,
  unit: string,
  minimum: number
}

@Component({
  selector: 'app-volume-discount-rate-request-form',
  templateUrl: './volume-discount-rate-request-form.component.html',
  styleUrls: ['./volume-discount-rate-request-form.component.css']
})
export class VolumeDiscountRateRequestFormComponent extends LoadingAndErrors {
  data: VolumeDiscountForm;
  verifiedPartnerId: boolean = false;
  userRole;
  volumeDiscountRate;
  discountRateRequest;
  match;
  discounts;
  standardDiscounts;
  merchantTypes: typeof MERCHANT_TYPE = MERCHANT_TYPE;
  paymentTypes: typeof PAYMENT_TYPE = PAYMENT_TYPE;

  constructor(
    public modal: BsModalRef,
    public productService: ProductsService,
    private userDataApi: UserDataApi,
    private partnerApi: PartnerApi,
    private discountRateRequestApi: DiscountRequestApi,
    private volumeDiscountRateApi: VolumeDiscountRateApi,
    private volumeDiscountRateRequestApi: VolumeDiscountRateRequestApi,
    private standardDiscountApi: StandardDiscountApi,
    private merchantTypePipe: MerchantTypePipe,
    private paymentTypePipe: PaymentTypePipe,
    private sapMerchantTypePipe: SapMerchantTypePipe,
    private sapPaymentTypePipe: SapPaymentTypePipe,
    private discountsService: DiscountsService,
    private toast: ToastrService
  ) {
    super();
    this.discountsService.fetchMaxDiscounts();
  }

  ngOnInit() {
    this.data = this.data || {
      productType: undefined,
      partnerId: undefined,
      merchantType: undefined,
      paymentType: undefined,
      discount: 0,
      unit: VOLUME_DISCOUNT_UNITS.PERCENT,
      minimum: 0
    };
    if (this.data.id) {
      this.fetchDiscounts(this.data.partnerId, this.data.merchantType, this.data.paymentType);
    }
  }

  dismiss(reason: string) {
    this.modal.content.reason = reason;
    this.modal.hide();
  }

  invalidate() {
    this.verifiedPartnerId = false;
  }

  close(form) {
    if (form.valid) {
      const {
        id,
        partnerId,
        productType,
        merchantType,
        paymentType,
        discount,
        unit,
        minimum
      } = this.data;
      this.clearErrors();
      if (this.verifiedPartnerId || id) {
        this.loading.submit = true;
        (id ?
          this.volumeDiscountRateRequestApi
            .patchAttributes(id, {
              merchantType,
              paymentType,
              discount,
              unit,
              minimum
            }) :
          this.volumeDiscountRateRequestApi
            .create({
              productType: productType.name,
              partnerId,
              merchantType,
              paymentType,
              discount,
              unit,
              minimum
            })
        ).pipe(finalize(() => {
          this.loading.submit = false;
        }))
        .subscribe(response => {
          this.modal.content.result = response;
          this.modal.hide();
        }, error => {
          if (error && error.message) {
            this.errors.submit = error.message.indexOf('Unique combination') >= 0 ?
              'Already have a volume discount rate for the product' :
              (error.message || 'Failed to create volume discount rate.');
          } else {
            this.errors.submit = 'Failed to create volume discount rate.';
          }
        });
      } else {
        this.loading.verify = true;
        this.partnerApi
          .exists(partnerId)
          .pipe(finalize(() => {
            this.loading.verify = false;
          }))
          .subscribe((type: any) => {
            if (type.exists == PARTNER_OBJECT_TYPE.MERCHANT) {
              this.loadUserData(partnerId);
            } else {
              this.errors.verify = 'Cannot setup volume discount rate for non merchant partners.';
            }
          }, error => {
            if (error && error.message && error.message === 'Account already exists.') {
              if (error.type == PARTNER_OBJECT_TYPE.MERCHANT) {
                this.loadUserData(partnerId);
              } else {
                this.errors.verify = 'Cannot setup volume discount rate for non merchant partners.';
              }
            } else {
              this.errors.verify = error.message || 'Failed to verify partner id.';
            }
          });
      }
    }
  }

  loadUserData(partnerId) {
    this.loading.userRole = true;
    this.userRole = undefined;
    this.userDataApi
      .findOne({
        where: { partnerId: partnerId },
        include: { roles: 'role' }
      })
      .pipe(finalize(() => {
        this.loading.userRole = false;
      }))
      .subscribe((user: any) => {
        if (user && user.roles && user.roles.length) {
          this.userRole = user.roles[0];
          this.data.merchantType = this.userRole && this.userRole.role && this.userRole.role.code || MERCHANT_TYPE.GENERAL_MERCHANT;
          this.data.paymentType = this.userRole && this.userRole.paymentType || PAYMENT_TYPE.PAYMENT_FIRST;
          this.fetchDiscounts(partnerId, this.userRole.role.code, this.userRole.paymentType);
        } else {
          this.data.merchantType = MERCHANT_TYPE.GENERAL_MERCHANT;
          this.data.paymentType = PAYMENT_TYPE.PAYMENT_FIRST;
          this.loadVolumeDiscountRate(partnerId);
        }
      }, error => {
        this.toast.warning('Partner is not yet registered in salesviewpoint.', 'Merchant Details');
        this.loadVolumeDiscountRate(partnerId);
      });
  }

  loadVolumeDiscountRate(partnerId) {
    this.loading.volumeDiscountRate = true;
    this.volumeDiscountRate = undefined;
    this.volumeDiscountRateApi
      .findOne({
        where: { partnerId: partnerId }
      })
      .pipe(finalize(() => {
        this.loading.volumeDiscountRate = false;
      }))
      .subscribe((volumeDiscountRate: any) => {
        this.volumeDiscountRate = volumeDiscountRate;
        if (volumeDiscountRate) {
          this.data.merchantType = volumeDiscountRate.merchantType || MERCHANT_TYPE.GENERAL_MERCHANT;
          this.data.paymentType = volumeDiscountRate.paymentType || PAYMENT_TYPE.PAYMENT_FIRST;
          this.fetchDiscounts(partnerId, this.data.merchantType, this.data.paymentType);
        } else {
          this.loadVolumeDiscountRateRequest(partnerId);
        }
      }, error => {
        this.loadVolumeDiscountRateRequest(partnerId);
      });
  }

  loadVolumeDiscountRateRequest(partnerId) {
    this.loading.volumeDiscountRateRequest = true;
    this.match = undefined;
    this.volumeDiscountRateRequestApi
      .findOne({
        where: { partnerId: partnerId }
      })
      .pipe(finalize(() => {
        this.loading.volumeDiscountRateRequest = false;
      }))
      .subscribe((volumeDiscountRateRequest: any) => {
        this.match = volumeDiscountRateRequest;
        if (volumeDiscountRateRequest) {
          this.data.merchantType = volumeDiscountRateRequest.merchantType || MERCHANT_TYPE.GENERAL_MERCHANT;
          this.data.paymentType = volumeDiscountRateRequest.paymentType || PAYMENT_TYPE.PAYMENT_FIRST;
          this.fetchDiscounts(partnerId, this.data.merchantType, this.data.paymentType);
        } else {
          this.fetchDiscounts(partnerId);
        }
      }, error => {
        this.fetchDiscounts(partnerId);
      });
  }

  loadDiscountRateRequest(partnerId) {
    this.loading.discountRateRequest = true;
    this.discountRateRequest = undefined;
    this.discountRateRequestApi
      .findOne({
        where: { partnerId: partnerId }
      })
      .pipe(finalize(() => {
        this.loading.discountRateRequest = false;
      }))
      .subscribe((discountRateRequest: any) => {
        this.discountRateRequest = discountRateRequest;
        this.data.merchantType = discountRateRequest && MERCHANT_TYPE[this.sapMerchantTypePipe.transform(discountRateRequest.merchantType).split(' ').join('_')] || MERCHANT_TYPE.GENERAL_MERCHANT;
        this.data.paymentType = discountRateRequest && PAYMENT_TYPE[this.sapPaymentTypePipe.transform(discountRateRequest.paymentType).split(' ').join('_')] || PAYMENT_TYPE.PAYMENT_FIRST;
        this.fetchStandardDiscounts(this.data.merchantType, this.data.paymentType);
      }, error => {
        this.data.merchantType = this.userRole && this.userRole.role && this.userRole.role.code || MERCHANT_TYPE.GENERAL_MERCHANT;
        this.data.paymentType = this.userRole && this.userRole.paymentType || PAYMENT_TYPE.PAYMENT_FIRST;
        this.fetchStandardDiscounts();
      });
  }

  fetchDiscounts(partnerId, merchantType?, paymentType?) {
    this.loading.discounts = true;
    this.clearErrors();
    this.partnerApi
      .getDiscount(partnerId, undefined, true)
      .pipe(finalize(() => {
        this.loading.discounts = false;
      }))
      .subscribe(response => {
        if (merchantType && paymentType) {
          this.discounts = response.filter(discount => {
            return this.merchantTypePipe.transform(merchantType) === this.sapMerchantTypePipe.transform(discount.merchantType) &&
            this.paymentTypePipe.transform(paymentType) === this.sapPaymentTypePipe.transform(discount.paymentType)
          });
          this.data.merchantType = merchantType || MERCHANT_TYPE.GENERAL_MERCHANT;
          this.data.paymentType = paymentType || PAYMENT_TYPE.PAYMENT_FIRST;
          this.fetchStandardDiscounts(merchantType, paymentType);
        } else {
          this.discounts = response;
          if (this.discounts && this.discounts.length) {
            this.data.merchantType = this.discounts[0] && MERCHANT_TYPE[this.sapMerchantTypePipe.transform(this.discounts[0].merchantType).split(' ').join('_')] || MERCHANT_TYPE.GENERAL_MERCHANT;
            this.data.paymentType = this.discounts[0] && PAYMENT_TYPE[this.sapPaymentTypePipe.transform(this.discounts[0].paymentType).split(' ').join('_')] || PAYMENT_TYPE.PAYMENT_FIRST;
            this.fetchStandardDiscounts(this.data.merchantType, this.data.paymentType)
          } else {
            this.loadDiscountRateRequest(partnerId);
          }
        }
      },
      error => {
        this.errors.discounts = 'An error occured while fetching the discounts.';
      });
  }

  fetchStandardDiscounts(merchantType?, paymentType?) {
    this.loading.standardDiscounts = true;
    this.clearErrors();
    this.standardDiscountApi
      .find({
        where: { paymentType, merchantType }
      })
      .pipe(finalize(() => {
        this.loading.standardDiscounts = false;
        this.verifiedPartnerId = true;
      }))
      .subscribe(response => {
        this.standardDiscounts = response;
      },
      error => {
        this.errors.standardDiscounts = 'An error occured while fetching the standard discounts.';
      });
  }

  getMaxDiscountOfProduct(productType, merchantType, paymentType) {
    if (productType && productType.name && SYSTEM.MAX_DISCOUNT[productType.name]) {
      let discount = (this.discounts || [])
        .filter(discount => {
          return discount.productType === productType.name &&
            MERCHANT_TYPE[this.sapMerchantTypePipe.transform(discount.merchantType).split(' ').join('_')] === merchantType &&
            PAYMENT_TYPE[this.sapPaymentTypePipe.transform(discount.paymentType).split(' ').join('_')] === paymentType;
        })[0];
      if (!discount) {
        discount = (this.standardDiscounts || [])
          .filter(standardDiscount => {
            return standardDiscount.productType === productType.name &&
              standardDiscount.merchantType === merchantType &&
              standardDiscount.paymentType === paymentType;
          })
          .map(standardDiscount => {
            return {
              rate: standardDiscount.discount,
              unit: 'P' // DEFAULTS TO PERCENT
            }
          })[0];
      }
      return SYSTEM.MAX_DISCOUNT[productType.name] - parseFloat(discount && discount.rate || 0);
    } else {
      return 100;
    }
  }
}
